import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShoppingCart, Trash2, DollarSign, CheckCircle, Copy, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/api';

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(location.state?.cart || []);
  const [step, setStep] = useState(1); // 1: Review, 2: Info, 3: Payment
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  const cashAppTag = '$vintageavemusic';

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * (item.quantity || 1), 0);
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty < 1) return;
    const newCart = cart.map(item => 
      item.id === itemId ? { ...item, quantity: newQty } : item
    );
    setCart(newCart);
  };

  const removeItem = (itemId) => {
    const newCart = cart.filter(item => item.id !== itemId);
    setCart(newCart);
    toast.success('Item removed from cart');
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const submitOrder = async () => {
    setLoading(true);
    try {
      const orderData = {
        customer: formData,
        items: cart.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1
        })),
        total: getCartTotal(),
        payment_method: 'cashapp',
        cashapp_tag: cashAppTag
      };

      const response = await axios.post(`${API}/orders`, orderData);
      setOrderId(response.data.order_id);
      setOrderComplete(true);
      toast.success('Order submitted! Please complete payment via CashApp.');
    } catch (error) {
      toast.error('Failed to submit order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="checkout-empty">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <ShoppingCart className="w-24 h-24 text-[#3D302C] mx-auto mb-6" />
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#FDFBF7] mb-4">
            Your Cart is Empty
          </h1>
          <p className="text-[#A89B95] mb-8">Add some items to your cart to checkout.</p>
          <Link to="/shop">
            <Button className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full px-8">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="checkout-success">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-center mb-8"
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#FDFBF7] mb-2">
              Order Submitted!
            </h1>
            <p className="text-[#A89B95]">Order ID: <span className="text-[#CFB53B]">{orderId}</span></p>
          </motion.div>

          <Card className="bg-[#2C221F] border-[#D97736]">
            <CardContent className="p-8">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-4 text-center">
                Complete Your Payment
              </h2>
              
              <div className="bg-[#1A1412] rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#00D632] rounded-xl flex items-center justify-center">
                    <DollarSign className="text-white" size={28} />
                  </div>
                  <span className="text-[#FDFBF7] text-2xl font-bold">CashApp</span>
                </div>
                
                <div className="text-center">
                  <p className="text-[#A89B95] mb-2">Send payment to:</p>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="text-[#00D632] text-3xl font-bold">{cashAppTag}</span>
                    <button 
                      onClick={() => copyToClipboard(cashAppTag)}
                      className="p-2 bg-[#3D302C] rounded-lg hover:bg-[#4D403C] transition-colors"
                    >
                      <Copy size={18} className="text-[#A89B95]" />
                    </button>
                  </div>
                  
                  <div className="bg-[#D97736] text-white rounded-xl p-4 mb-4">
                    <p className="text-lg">Amount Due:</p>
                    <p className="text-4xl font-bold">${getCartTotal().toFixed(2)}</p>
                  </div>
                  
                  <p className="text-[#CFB53B] text-sm">
                    Include your Order ID in the payment note: <strong>{orderId}</strong>
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-[#A89B95] text-sm">
                <p>📱 Open CashApp and send ${getCartTotal().toFixed(2)} to {cashAppTag}</p>
                <p>📝 Include Order ID: {orderId} in the note</p>
                <p>📧 You'll receive confirmation at {formData.email}</p>
                <p>🚚 Orders ship within 3-5 business days after payment confirmation</p>
              </div>

              <div className="mt-8 text-center">
                <Link to="/shop">
                  <Button className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full px-8">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mb-4">
            Checkout
          </h1>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mt-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-[#D97736] text-white' : 'bg-[#3D302C] text-[#A89B95]'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 ${step > s ? 'bg-[#D97736]' : 'bg-[#3D302C]'}`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-8 mt-2 text-sm text-[#A89B95]">
            <span>Review</span>
            <span>Info</span>
            <span>Payment</span>
          </div>
        </div>

        {/* Step 1: Review Cart */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="bg-[#2C221F] border-[#3D302C]">
              <CardContent className="p-6">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-6">
                  Your Cart ({cart.length} items)
                </h2>
                
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.id || `${item.name}-${item.price}`} className="flex items-center gap-4 p-4 bg-[#1A1412] rounded-xl">
                      <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h3 className="text-[#FDFBF7] font-semibold">{item.name}</h3>
                        <p className="text-[#D97736] font-bold">${item.price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                          className="w-8 h-8 bg-[#3D302C] rounded-lg text-[#FDFBF7] hover:bg-[#4D403C]"
                        >-</button>
                        <span className="text-[#FDFBF7] w-8 text-center">{item.quantity || 1}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                          className="w-8 h-8 bg-[#3D302C] rounded-lg text-[#FDFBF7] hover:bg-[#4D403C]"
                        >+</button>
                      </div>
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-[#3D302C]">
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-[#FDFBF7]">Total:</span>
                    <span className="text-[#CFB53B]">${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 flex gap-4">
                  <Link to="/shop" className="flex-1">
                    <Button variant="outline" className="w-full border-[#3D302C] text-[#FDFBF7] hover:bg-[#3D302C]">
                      <ArrowLeft size={18} className="mr-2" /> Continue Shopping
                    </Button>
                  </Link>
                  <Button 
                    onClick={() => setStep(2)} 
                    className="flex-1 bg-[#D97736] hover:bg-[#F08F4D] text-white"
                  >
                    Proceed to Checkout
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 2: Customer Info */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="bg-[#2C221F] border-[#3D302C]">
              <CardContent className="p-6">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-6">
                  Shipping Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-[#FDFBF7]">Full Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FDFBF7]">Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FDFBF7]">Phone *</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#FDFBF7]">Street Address *</Label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                      placeholder="123 Main St"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FDFBF7]">City *</Label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                      placeholder="Detroit"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#FDFBF7]">State *</Label>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                        placeholder="MI"
                      />
                    </div>
                    <div>
                      <Label className="text-[#FDFBF7]">ZIP *</Label>
                      <Input
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        required
                        className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                        placeholder="48201"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex gap-4">
                  <Button 
                    onClick={() => setStep(1)} 
                    variant="outline" 
                    className="flex-1 border-[#3D302C] text-[#FDFBF7] hover:bg-[#3D302C]"
                  >
                    <ArrowLeft size={18} className="mr-2" /> Back
                  </Button>
                  <Button 
                    onClick={() => {
                      if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.state || !formData.zip) {
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      setStep(3);
                    }} 
                    className="flex-1 bg-[#D97736] hover:bg-[#F08F4D] text-white"
                  >
                    Continue to Payment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Order Summary */}
              <Card className="bg-[#2C221F] border-[#3D302C]">
                <CardContent className="p-6">
                  <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#FDFBF7] mb-4">
                    Order Summary
                  </h2>
                  
                  <div className="space-y-3 mb-4">
                    {cart.map((item) => (
                      <div key={item.id || `${item.name}-${item.price}`} className="flex justify-between text-sm">
                        <span className="text-[#A89B95]">{item.name} x{item.quantity || 1}</span>
                        <span className="text-[#FDFBF7]">${(item.price * (item.quantity || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t border-[#3D302C]">
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-[#FDFBF7]">Total:</span>
                      <span className="text-[#CFB53B]">${getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-[#1A1412] rounded-lg">
                    <h3 className="text-[#FDFBF7] font-semibold mb-2">Shipping To:</h3>
                    <p className="text-[#A89B95] text-sm">
                      {formData.name}<br />
                      {formData.address}<br />
                      {formData.city}, {formData.state} {formData.zip}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card className="bg-[#2C221F] border-[#D97736]">
                <CardContent className="p-6">
                  <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#FDFBF7] mb-4">
                    Payment Method
                  </h2>
                  
                  <div className="space-y-4">
                    {/* CashApp Option */}
                    <div className="bg-[#1A1412] rounded-xl p-4 text-center">
                      <div className="w-12 h-12 bg-[#00D632] rounded-xl flex items-center justify-center mx-auto mb-3">
                        <DollarSign className="text-white" size={24} />
                      </div>
                      <h3 className="text-[#FDFBF7] text-lg font-bold mb-1">CashApp</h3>
                      <div className="bg-[#3D302C] rounded-lg p-2">
                        <span className="text-[#00D632] text-lg font-bold">{cashAppTag}</span>
                      </div>
                    </div>

                    {/* Venmo Option */}
                    <div className="bg-[#1A1412] rounded-xl p-4 text-center">
                      <div className="w-12 h-12 bg-[#008CFF] rounded-xl flex items-center justify-center mx-auto mb-3">
                        <span className="text-white text-lg font-bold">V</span>
                      </div>
                      <h3 className="text-[#FDFBF7] text-lg font-bold mb-1">Venmo</h3>
                      <div className="bg-[#3D302C] rounded-lg p-2">
                        <span className="text-[#008CFF] text-lg font-bold">@VINTAGEAVERECORDS</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-[#A89B95] text-sm text-center mt-4">
                    After placing your order, complete payment via CashApp or Venmo
                  </p>

                  <div className="mt-6 flex gap-4">
                    <Button 
                      onClick={() => setStep(2)} 
                      variant="outline" 
                      className="flex-1 border-[#3D302C] text-[#FDFBF7] hover:bg-[#3D302C]"
                    >
                      <ArrowLeft size={18} className="mr-2" /> Back
                    </Button>
                    <Button 
                      onClick={submitOrder}
                      disabled={loading}
                      className="flex-1 bg-[#D97736] hover:bg-[#F08F4D] text-white"
                    >
                      {loading ? 'Processing...' : 'Place Order'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
